<template>
  <div class="app-feedback" aria-live="polite">
    <!-- Native-style toasts -->
    <transition-group
      name="app-toast"
      tag="div"
      class="app-toast-stack"
    >
      <div
        v-for="t in toasts"
        :key="t.id"
        class="app-toast"
        :class="'app-toast--' + t.type"
        role="status"
        @click="dismissToast(t.id)"
      >
        <span class="app-toast__icon" aria-hidden="true">
          <svg v-if="t.type === 'success'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
            <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <svg v-else-if="t.type === 'error'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
            <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" />
          </svg>
          <svg v-else-if="t.type === 'warning'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
            <path d="M12 8v5" stroke-linecap="round" />
            <circle cx="12" cy="16.5" r="1" fill="currentColor" stroke="none" />
            <path d="M10.3 4.3L2.8 18a2 2 0 001.7 3h15a2 2 0 001.7-3L13.7 4.3a2 2 0 00-3.4 0z" stroke-linejoin="round" />
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v5" stroke-linecap="round" />
            <circle cx="12" cy="16.5" r="1" fill="currentColor" stroke="none" />
          </svg>
        </span>
        <div class="app-toast__body">
          <p v-if="t.title" class="app-toast__title">{{ t.title }}</p>
          <p class="app-toast__msg">{{ t.message }}</p>
        </div>
      </div>
    </transition-group>

    <!-- Native-style alert dialog -->
    <transition name="app-alert">
      <div
        v-if="alert"
        class="app-alert-root"
        role="dialog"
        aria-modal="true"
        @click.self="resolveAlert(false)"
      >
        <div class="app-alert-card">
          <h3 class="app-alert-title">{{ alert.title }}</h3>
          <p class="app-alert-msg">{{ alert.message }}</p>
          <div class="app-alert-actions">
            <button
              v-if="alert.showCancel"
              type="button"
              class="app-alert-btn app-alert-btn--ghost"
              @click="resolveAlert(false)"
            >
              {{ alert.cancelText }}
            </button>
            <button
              type="button"
              class="app-alert-btn app-alert-btn--ok"
              @click="resolveAlert(true)"
            >
              {{ alert.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
let seed = 0

export default {
  name: 'AppFeedback',
  data () {
    return {
      toasts: [],
      alert: null,
      alertResolver: null
    }
  },
  methods: {
    showToast ({ title = '', message = '', type = 'info', duration = 2800 } = {}) {
      const id = ++seed
      const item = { id, title, message, type }
      this.toasts.push(item)
      if (this.toasts.length > 3) {
        this.toasts.shift()
      }
      if (duration > 0) {
        setTimeout(() => this.dismissToast(id), duration)
      }
      return id
    },
    dismissToast (id) {
      this.toasts = this.toasts.filter(t => t.id !== id)
    },
    showAlert ({
      title = '',
      message = '',
      confirmText = 'OK',
      cancelText = 'Цуцлах',
      showCancel = false
    } = {}) {
      return new Promise((resolve) => {
        this.alertResolver = resolve
        this.alert = { title, message, confirmText, cancelText, showCancel }
      })
    },
    resolveAlert (ok) {
      const resolver = this.alertResolver
      this.alert = null
      this.alertResolver = null
      if (resolver) resolver(ok)
    }
  }
}
</script>

<style scoped>
.app-feedback {
  pointer-events: none;
}

.app-toast-stack {
  position: fixed;
  left: 50%;
  bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  z-index: 10050;
  display: flex;
  flex-direction: column-reverse;
  gap: 10px;
  width: min(420px, calc(100vw - 24px));
  pointer-events: none;
}

.app-toast {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(11, 31, 58, 0.94);
  color: #fff;
  box-shadow: 0 12px 40px rgba(11, 31, 58, 0.28);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.app-toast--success {
  background: rgba(15, 118, 110, 0.96);
}

.app-toast--error {
  background: rgba(185, 28, 28, 0.96);
}

.app-toast--warning {
  background: rgba(180, 83, 9, 0.96);
}

.app-toast__icon {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  margin-top: 1px;
}

.app-toast__icon svg {
  width: 100%;
  height: 100%;
  display: block;
}

.app-toast__body {
  min-width: 0;
  flex: 1;
}

.app-toast__title {
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}

.app-toast__msg {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  opacity: 0.95;
}

.app-toast-enter-active,
.app-toast-leave-active {
  transition: all 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.app-toast-enter {
  opacity: 0;
  transform: translateY(18px) scale(0.96);
}

.app-toast-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

.app-toast-move {
  transition: transform 0.28s ease;
}

.app-alert-root {
  pointer-events: auto;
  position: fixed;
  inset: 0;
  z-index: 10060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(11, 31, 58, 0.45);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.app-alert-card {
  width: min(320px, 100%);
  background: #fff;
  border-radius: 20px;
  padding: 22px 20px 14px;
  text-align: center;
  box-shadow: 0 24px 60px rgba(11, 31, 58, 0.25);
}

.app-alert-title {
  margin: 0;
  font-family: Syne, Manrope, system-ui, sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #0B1F3A;
}

.app-alert-msg {
  margin: 10px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: #5A6B7F;
  font-weight: 500;
}

.app-alert-actions {
  display: flex;
  gap: 8px;
  margin-top: 18px;
}

.app-alert-btn {
  flex: 1;
  min-height: 48px;
  border: 0;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.app-alert-btn--ok {
  background: #0F766E;
  color: #fff;
}

.app-alert-btn--ghost {
  background: #F3F6FA;
  color: #0B1F3A;
}

.app-alert-enter-active,
.app-alert-leave-active {
  transition: opacity 0.22s ease;
}

.app-alert-enter-active .app-alert-card,
.app-alert-leave-active .app-alert-card {
  transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease;
}

.app-alert-enter,
.app-alert-leave-to {
  opacity: 0;
}

.app-alert-enter .app-alert-card,
.app-alert-leave-to .app-alert-card {
  opacity: 0;
  transform: scale(0.92) translateY(8px);
}
</style>
